import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import axios from 'axios';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_2452ebe5-8b88-4541-a707-a9ae800f68db/artifacts/oj0fg93b_ChatGPT%20Image%2011%20mrt%202026%2C%2015_21_20.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResendVerificationPage = () => {
  const { i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error(i18n.language === 'nl' ? 'Voer uw e-mailadres in' : 'Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/resend-verification?email=${encodeURIComponent(email)}`);
      setSubmitted(true);
    } catch (error) {
      // Handle rate limit specifically
      if (error.response?.status === 429) {
        toast.error(error.response.data.detail || (i18n.language === 'nl' 
          ? 'Te veel verzoeken. Probeer het later opnieuw.' 
          : 'Too many requests. Please try again later.'));
      } else {
        // Always show success to prevent email enumeration
        setSubmitted(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-navy-800 relative overflow-hidden" data-testid="resend-verification-page">
      {/* Background */}
      <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-10" />
      
      {/* Content */}
      <div className="container-custom py-20 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md mx-auto"
        >
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/">
              <img 
                src={LOGO_URL} 
                alt="Elementary Cargo" 
                className="h-20 mx-auto mb-4"
              />
            </Link>
            <h1 className="font-outfit text-2xl font-bold text-white mb-2">
              {i18n.language === 'nl' ? 'Verificatie Opnieuw Versturen' : 'Resend Verification'}
            </h1>
            <p className="text-steel-400">
              {i18n.language === 'nl' 
                ? 'Voer uw e-mailadres in om een nieuwe verificatielink te ontvangen' 
                : 'Enter your email to receive a new verification link'}
            </p>
          </div>

          <Card className="shadow-2xl border-0">
            <CardContent className="p-8">
              {!submitted ? (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="resend-form">
                  <div>
                    <Label className="text-navy-800">
                      {i18n.language === 'nl' ? 'E-mailadres' : 'Email Address'}
                    </Label>
                    <div className="relative mt-2">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-steel-400" aria-hidden="true" />
                      <Input
                        type="email"
                        placeholder={i18n.language === 'nl' ? 'uw@email.nl' : 'your@email.com'}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 h-12"
                        data-testid="resend-email-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 bg-gold hover:bg-gold/90 text-navy-900 font-semibold"
                    disabled={loading}
                  >
                    {loading ? (
                      i18n.language === 'nl' ? 'Verzenden...' : 'Sending...'
                    ) : (
                      i18n.language === 'nl' ? 'Verificatielink Versturen' : 'Send Verification Link'
                    )}
                  </Button>

                  <div className="text-center pt-4">
                    <Link
                      to="/login"
                      className="text-sm text-steel-600 hover:text-gold transition-colors inline-flex items-center gap-2"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      {i18n.language === 'nl' ? 'Terug naar Inloggen' : 'Back to Login'}
                    </Link>
                  </div>
                </form>
              ) : (
                <div className="text-center py-6" data-testid="resend-success">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-navy-800 mb-2">
                    {i18n.language === 'nl' ? 'E-mail Verzonden!' : 'Email Sent!'}
                  </h3>
                  <p className="text-steel-600 mb-6">
                    {i18n.language === 'nl' 
                      ? 'Als er een account bestaat met dit e-mailadres, ontvangt u een verificatielink.' 
                      : 'If an account exists with this email, you will receive a verification link.'}
                  </p>
                  <Link
                    to="/login"
                    className="text-gold hover:text-gold/80 font-medium inline-flex items-center gap-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    {i18n.language === 'nl' ? 'Terug naar Inloggen' : 'Back to Login'}
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default ResendVerificationPage;
